import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as Delete } from 'assets/icons/Delete.svg';
import { boldElement } from 'components/ConfirmPopup';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useLeaveWorkspaceWithAssign from 'hooks/useLeaveWorkspace/useLeaveWorkspaceWithAssign';
import OptionsDropdown from 'modules/MediaDevicesSettings/OptionsDropdown';
import { getWorkspaceName } from 'modules/Workspaces/WorkspaceSettingsModal/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

const LeaveAndAssignAdminModal = create(() => {
  const { t } = useTranslation();
  const { remove } = useModal(LeaveAndAssignAdminModal);
  const { matrixClient } = useMatrixContext();
  const { workspaceId } = useWorkspacesContext({ requireWorkspaceId: true });
  const { leaveWithAssign, dropdownOptions, isDisabled, isLoading } = useLeaveWorkspaceWithAssign();

  const workspaceName = getWorkspaceName(matrixClient, workspaceId);

  const handleConfirm = () =>
    leaveWithAssign().then(() => {
      remove();
    });

  return (
    <Popup
      closePopup={remove}
      className="md:!m-auto md:h-auto"
      wrapperId="leave-and-assign-workspace-modal"
      isVisible
      isDesktopViewportScrollable
    >
      <div className="flex h-full w-full flex-col rounded-t-2xl bg-white shadow-xs md:h-auto md:max-w-[23rem] md:rounded-2xl">
        <Popup.Header className="items-center justify-center md:!hidden">
          {t('workspaces.leave_workspace')}
          <Popup.CloseIcon closePopup={remove} />
        </Popup.Header>
        <div className="h-full w-full space-y-4 overflow-y-auto p-4 md:overflow-visible">
          <div className="flex flex-col items-center space-y-4 p-4">
            <Delete className="h-8 w-8 shrink-0 stroke-current stroke-2 text-tertiary-50" />
            <p className="text-gray-80 text-size-sm w-full break-words text-center">
              <Trans
                i18nKey="workspaces.only_owner_in_workspace_desc"
                components={{ bold: boldElement }}
                values={{ workspaceName }}
              />
            </p>
          </div>
          <div className="flex flex-col space-y-2">
            <span className="text-size-sm font-medium text-black">{t('workspaces.assign_owner')}</span>
            <OptionsDropdown options={dropdownOptions} selectClassName="h-12" />
          </div>
        </div>
        <div className="w-full flex gap-2 justify-center p-4 border-t border-gray-30 md:border-0">
          <Button variant="secondaryTertiary" size="sm" onClick={remove} className="w-full">
            {t('cancel')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isDisabled}
            className="w-full"
          >
            {t('leave')}
          </Button>
        </div>
      </div>
    </Popup>
  );
});

export default LeaveAndAssignAdminModal;
